import { ACTIVE_ROUTE_MAP, type RouteEntry } from "./routeMapData"

type WaypointRole = "creator" | "admin" | "client" | "operator" | null

type WaypointContext = {
  role: WaypointRole
  isCreator: boolean
}

const EXCLUDED_WAYPOINT_PATHS = new Set<string>([
  "/onboarding",
])

// Creators are the platform team — they have no tenant company trace.
// They edit trace via /creator/trace; /my-trace is a tenant-only view.
const CREATOR_EXCLUDED_WAYPOINT_PATHS = new Set<string>([
  "/my-trace",
])

const DEFAULT_WAYPOINT_PATHS = [
  // Public
  "/apply",
  "/login",
  // Core (all except onboarding)
  "/",
  "/schedule",
  "/activities",
  "/properties",
  "/settings",
  "/docs",
  "/trace",
  "/my-trace",
  // Admin
  "/admin/users",
  "/admin/properties",
  "/admin/predictions",
]

function isRouteVisibleForRole(route: RouteEntry, ctx: WaypointContext): boolean {
  if (route.isRedirect) return false
  if (EXCLUDED_WAYPOINT_PATHS.has(route.path)) return false
  if (ctx.isCreator && CREATOR_EXCLUDED_WAYPOINT_PATHS.has(route.path)) return false

  // Non-creators should never see creator waypoints.
  if (!ctx.isCreator && route.quadrant === "creator") return false

  if (route.roles.includes("All")) return true
  if (ctx.isCreator || ctx.role === "creator") return true
  if (ctx.role === "admin") return route.roles.includes("Admin") || route.roles.includes("User") || route.roles.includes("Guest")
  if (ctx.role === "client" || ctx.role === "operator") return route.roles.includes("User") || route.roles.includes("Guest")
  return route.roles.includes("Guest")
}

export function getPickableWaypointRoutes(ctx: WaypointContext): RouteEntry[] {
  return ACTIVE_ROUTE_MAP.filter((route) => isRouteVisibleForRole(route, ctx))
}

export function sanitizeWaypointPaths(paths: string[], ctx: WaypointContext): string[] {
  const allowed = new Set(getPickableWaypointRoutes(ctx).map((route) => route.path))
  const seen = new Set<string>()
  const cleaned: string[] = []

  for (const path of paths) {
    if (!allowed.has(path) || seen.has(path)) continue
    seen.add(path)
    cleaned.push(path)
  }

  return cleaned
}

export function getDefaultWaypointPaths(ctx: WaypointContext): string[] {
  const allowed = new Set(getPickableWaypointRoutes(ctx).map((route) => route.path))
  return DEFAULT_WAYPOINT_PATHS.filter((path) => allowed.has(path))
}
